import { escape_html } from './html'
import { icon } from './icons'
import { open_modal, type ModalSize } from './modal'

export type ComboOption = {
  id: string
  label: string
  hint?: string | null
  group?: string | null
}

type CommonOptions = {
  title: string
  eyebrow?: string
  placeholder?: string
  empty_hint?: string
  size?: ModalSize
  search?: (query: string) => Promise<ComboOption[]> | ComboOption[]
  initial?: ComboOption[]
  allow_create?: {
    label?: string
    on_create: (value: string) => Promise<ComboOption | null> | ComboOption | null
  }
}

export type SingleSelectOptions = CommonOptions & {
  on_pick: (option: ComboOption) => void
  on_cancel?: () => void
}

export type MultiSelectOptions = CommonOptions & {
  selected: ComboOption[]
  on_change?: (selected: ComboOption[]) => void
  on_done: (selected: ComboOption[]) => void
  on_cancel?: () => void
}

function list_html(options: ComboOption[], highlight: number, selected_ids: Set<string>, allow_create_label: string | null): string {
  if (!options.length && !allow_create_label) {
    return `<li data-combobox-empty class="px-3 py-4 text-center text-sm text-muted">Nenhum resultado.</li>`
  }
  const items = options.map((opt, idx) => {
    const is_highlighted = idx === highlight
    const is_selected = selected_ids.has(opt.id)
    const cls = is_highlighted
      ? 'bg-ink text-canvas'
      : is_selected
        ? 'bg-surface2 text-ink'
        : 'text-ink hover:bg-surface2'
    const hint_cls = is_highlighted ? 'text-canvas' : 'text-muted'
    return `
      <li>
        <button type="button" data-combobox-pick="${escape_html(opt.id)}" data-idx="${idx}"
                class="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition ${cls}">
          <span class="min-w-0">
            <span class="block truncate">${escape_html(opt.label)}</span>
            ${opt.hint ? `<span class="block truncate text-[13px] ${hint_cls}">${escape_html(opt.hint)}</span>` : ''}
          </span>
          ${is_selected ? `<span class="shrink-0 ${is_highlighted ? 'text-canvas' : 'text-ink'}">${icon('check', 'h-4 w-4')}</span>` : ''}
        </button>
      </li>`
  }).join('')

  const create_row = allow_create_label
    ? `<li>
        <button type="button" data-combobox-create
                class="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink hover:bg-surface2">
          ${icon('plus', 'h-4 w-4')} ${escape_html(allow_create_label)}
        </button>
      </li>`
    : ''

  return items + create_row
}

function body_html(state: { query: string; placeholder: string; empty_hint: string }, selected_labels: string[]): string {
  return `
    <div class="grid gap-3">
      <label class="grid gap-1">
        <span class="text-xs font-semibold uppercase tracking-wider text-muted">Buscar</span>
        <input data-combobox-search type="search" autocomplete="off" placeholder="${escape_html(state.placeholder)}"
               value="${escape_html(state.query)}"
               class="h-10 rounded-md border border-border bg-surface px-3 text-sm text-ink outline-none focus:border-ink" />
      </label>
      ${selected_labels.length
        ? `<div data-combobox-chips class="flex flex-wrap gap-1.5 rounded-md border border-border bg-surface2 px-2 py-2">${
            selected_labels.map((label, idx) => `
              <span class="inline-flex h-7 items-center gap-1 rounded-full bg-surface px-2 text-[11px] font-semibold text-ink">
                <span>${escape_html(label)}</span>
                <button type="button" data-combobox-remove="${idx}" class="inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-surface2" aria-label="Remover">${icon('x', 'h-3 w-3')}</button>
              </span>`).join('')
          }</div>`
        : ''}
      <ul data-combobox-list class="max-h-80 overflow-auto divide-y divide-border rounded-md border border-border bg-surface text-sm"></ul>
      <p data-combobox-hint class="text-xs text-muted">${escape_html(state.empty_hint)}</p>
    </div>`
}

function single_footer_html(): string {
  return `
    <div class="flex items-center justify-between gap-3 text-xs text-muted">
      <span>Use ↑ ↓ Enter para selecionar · Esc fecha</span>
      <button type="button" data-combobox-cancel class="inline-flex h-9 items-center rounded-md border border-border bg-surface px-3 text-xs font-semibold text-ink hover:bg-surface2">Cancelar</button>
    </div>`
}

function multi_footer_html(selected_count: number): string {
  return `
    <div class="flex items-center justify-between gap-3 text-xs text-muted">
      <span>${selected_count} selecionada(s) · Esc fecha</span>
      <div class="flex items-center gap-2">
        <button type="button" data-combobox-cancel class="inline-flex h-9 items-center rounded-md border border-border bg-surface px-3 text-xs font-semibold text-ink hover:bg-surface2">Cancelar</button>
        <button type="button" data-combobox-done class="inline-flex h-9 items-center gap-2 rounded-md bg-brand px-3 text-xs font-bold uppercase tracking-wide text-brand-ink hover:opacity-90">${icon('check', 'h-4 w-4')} Aplicar</button>
      </div>
    </div>`
}

async function compute_options(
  query: string,
  search: CommonOptions['search'],
  initial: ComboOption[] | undefined
): Promise<ComboOption[]> {
  const trimmed = String(query || '').trim()
  if (search) {
    return Promise.resolve(search(trimmed)).then(items => Array.isArray(items) ? items : [])
  }
  const source = initial || []
  if (!trimmed) return source.slice(0, 100)
  const lower = trimmed.toLowerCase()
  return source.filter(opt =>
    opt.label.toLowerCase().includes(lower) ||
    (opt.hint || '').toLowerCase().includes(lower)
  ).slice(0, 100)
}

export function open_single_select_modal(opts: SingleSelectOptions): void {
  const handle = open_modal({
    size: opts.size || 'md',
    eyebrow: opts.eyebrow,
    title: opts.title,
    body: body_html(
      { query: '', placeholder: opts.placeholder || 'Digite para buscar…', empty_hint: opts.empty_hint || '' },
      []
    ),
    footer: single_footer_html(),
    on_close: () => opts.on_cancel?.()
  })

  let state = {
    query: '',
    options: [] as ComboOption[],
    highlight: 0
  }

  const search_input = handle.body.querySelector<HTMLInputElement>('[data-combobox-search]')!
  const list_el = handle.body.querySelector<HTMLUListElement>('[data-combobox-list]')!
  const cancel_btn = handle.footer.querySelector<HTMLButtonElement>('[data-combobox-cancel]')

  cancel_btn?.addEventListener('click', () => {
    handle.close()
  })

  const render_list = () => {
    list_el.innerHTML = list_html(state.options, state.highlight, new Set(), opts.allow_create?.label || null)
    const highlighted = list_el.querySelector<HTMLElement>(`[data-idx="${state.highlight}"]`)
    if (highlighted) highlighted.scrollIntoView({ block: 'nearest' })
  }

  const refresh = async () => {
    state.options = await compute_options(state.query, opts.search, opts.initial)
    if (state.highlight >= state.options.length) state.highlight = Math.max(0, state.options.length - 1)
    render_list()
  }

  list_el.addEventListener('click', async (event) => {
    const create_btn = (event.target as HTMLElement).closest<HTMLElement>('[data-combobox-create]')
    if (create_btn && opts.allow_create) {
      const created = await opts.allow_create.on_create(state.query.trim())
      if (created) {
        handle.close()
        opts.on_pick(created)
      }
      return
    }
    const btn = (event.target as HTMLElement).closest<HTMLElement>('[data-combobox-pick]')
    if (!btn) return
    const id = btn.dataset.comboboxPick
    const pick = state.options.find(o => o.id === id)
    if (!pick) return
    handle.close()
    opts.on_pick(pick)
  })

  let debounce: number | null = null
  search_input.addEventListener('input', () => {
    state.query = search_input.value
    if (debounce) window.clearTimeout(debounce)
    debounce = window.setTimeout(() => { void refresh() }, 150) as unknown as number
  })

  search_input.addEventListener('keydown', async (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      state.highlight = Math.min(state.highlight + 1, state.options.length - 1)
      render_list()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      state.highlight = Math.max(state.highlight - 1, 0)
      render_list()
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const pick = state.options[state.highlight]
      if (pick) {
        handle.close()
        opts.on_pick(pick)
      } else if (opts.allow_create && state.query.trim()) {
        const created = await opts.allow_create.on_create(state.query.trim())
        if (created) {
          handle.close()
          opts.on_pick(created)
        }
      }
    }
  })

  setTimeout(() => search_input.focus(), 10)
  void refresh()
}

export function open_multi_select_modal(opts: MultiSelectOptions): void {
  const selected = [...opts.selected]

  const handle = open_modal({
    size: opts.size || 'md',
    eyebrow: opts.eyebrow,
    title: opts.title,
    body: body_html(
      { query: '', placeholder: opts.placeholder || 'Digite para buscar…', empty_hint: opts.empty_hint || '' },
      selected.map(s => s.label)
    ),
    footer: multi_footer_html(selected.length),
    on_close: () => opts.on_cancel?.()
  })

  let state = {
    query: '',
    options: [] as ComboOption[],
    highlight: 0
  }

  const search_input = handle.body.querySelector<HTMLInputElement>('[data-combobox-search]')!
  const list_el = handle.body.querySelector<HTMLUListElement>('[data-combobox-list]')!
  const cancel_btn = handle.footer.querySelector<HTMLButtonElement>('[data-combobox-cancel]')
  const done_btn = handle.footer.querySelector<HTMLButtonElement>('[data-combobox-done]')

  const render_chips = () => {
    let chips = handle.body.querySelector<HTMLElement>('[data-combobox-chips]')
    const labels = selected.map(s => s.label)
    if (!labels.length) {
      chips?.remove()
      return
    }
    const html = labels.map((label, idx) => `
      <span class="inline-flex h-7 items-center gap-1 rounded-full bg-surface px-2 text-[11px] font-semibold text-ink">
        <span>${escape_html(label)}</span>
        <button type="button" data-combobox-remove="${idx}" class="inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-surface2" aria-label="Remover">${icon('x', 'h-3 w-3')}</button>
      </span>`).join('')
    if (!chips) {
      chips = document.createElement('div')
      chips.setAttribute('data-combobox-chips', '')
      chips.className = 'flex flex-wrap gap-1.5 rounded-md border border-border bg-surface2 px-2 py-2'
      search_input.parentElement?.parentElement?.insertBefore(chips, list_el)
    }
    chips.innerHTML = html
  }

  const update_footer = () => {
    handle.footer.innerHTML = multi_footer_html(selected.length)
    handle.footer.querySelector<HTMLButtonElement>('[data-combobox-cancel]')?.addEventListener('click', () => handle.close())
    handle.footer.querySelector<HTMLButtonElement>('[data-combobox-done]')?.addEventListener('click', () => {
      handle.close()
      opts.on_done(selected)
    })
  }

  const render_list = () => {
    list_el.innerHTML = list_html(state.options, state.highlight, new Set(selected.map(s => s.id)), opts.allow_create?.label || null)
    const highlighted = list_el.querySelector<HTMLElement>(`[data-idx="${state.highlight}"]`)
    if (highlighted) highlighted.scrollIntoView({ block: 'nearest' })
  }

  const toggle_selection = (option: ComboOption) => {
    const idx = selected.findIndex(s => s.id === option.id)
    if (idx >= 0) selected.splice(idx, 1)
    else selected.push(option)
    opts.on_change?.([...selected])
    render_chips()
    render_list()
    update_footer()
  }

  cancel_btn?.addEventListener('click', () => handle.close())
  done_btn?.addEventListener('click', () => {
    handle.close()
    opts.on_done(selected)
  })

  list_el.addEventListener('click', async (event) => {
    const create_btn = (event.target as HTMLElement).closest<HTMLElement>('[data-combobox-create]')
    if (create_btn && opts.allow_create) {
      const created = await opts.allow_create.on_create(state.query.trim())
      if (created) toggle_selection(created)
      return
    }
    const btn = (event.target as HTMLElement).closest<HTMLElement>('[data-combobox-pick]')
    if (!btn) return
    const id = btn.dataset.comboboxPick
    const option = state.options.find(o => o.id === id)
    if (!option) return
    toggle_selection(option)
  })

  handle.body.addEventListener('click', (event) => {
    const remove_btn = (event.target as HTMLElement).closest<HTMLElement>('[data-combobox-remove]')
    if (!remove_btn) return
    const idx = Number(remove_btn.dataset.comboboxRemove)
    if (!Number.isFinite(idx)) return
    selected.splice(idx, 1)
    opts.on_change?.([...selected])
    render_chips()
    render_list()
    update_footer()
  })

  let debounce: number | null = null
  search_input.addEventListener('input', () => {
    state.query = search_input.value
    if (debounce) window.clearTimeout(debounce)
    debounce = window.setTimeout(() => { void refresh() }, 150) as unknown as number
  })

  search_input.addEventListener('keydown', async (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      state.highlight = Math.min(state.highlight + 1, state.options.length - 1)
      render_list()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      state.highlight = Math.max(state.highlight - 1, 0)
      render_list()
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const option = state.options[state.highlight]
      if (option) {
        toggle_selection(option)
      } else if (opts.allow_create && state.query.trim()) {
        const created = await opts.allow_create.on_create(state.query.trim())
        if (created) toggle_selection(created)
      }
    }
  })

  const refresh = async () => {
    state.options = await compute_options(state.query, opts.search, opts.initial)
    if (state.highlight >= state.options.length) state.highlight = Math.max(0, state.options.length - 1)
    render_list()
  }

  setTimeout(() => search_input.focus(), 10)
  void refresh()
}
